import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { SAVE_REPLY_AS_PREFERENCE } from "../graphql/mutation";
import { REPLY_AS_PREFERENCE } from "../graphql/query";
import { setModuleFilters } from "../redux/slices/persistedFiltersSlice";
import { REPLY_RULE_TYPE } from "../components/engagements/activityCenter/replyAsUsers";

/**
 * Reply-As preferences (EAI-1530).
 *
 * - Default rules persist server-side per location via saveReplyAsPreference /
 *   replyAsPreference. Shape: { default_rules: [{ opportunity_type_id,
 *   rule_type, opportunity_user_type_id? }], lead_overrides: [] }. One rule per
 *   opportunity type; the mutation returns the full merged preferences, which we
 *   write back into the Apollo cache (no duplicate fetch).
 * - Per-lead override stays in the generic persistedFilters redux slice keyed by
 *   lead id (refresh-scoped) — the backend lead_overrides array is unused for now.
 */
const OVERRIDE_MODULE = "replyAsOverrides";

const EMPTY_PREFS = { default_rules: [], lead_overrides: [] };

const parsePrefs = raw => {
  if (!raw) return EMPTY_PREFS;
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch (e) {
      return EMPTY_PREFS;
    }
  }
  return {
    default_rules: Array.isArray(obj?.default_rules) ? obj.default_rules : [],
    lead_overrides: Array.isArray(obj?.lead_overrides) ? obj.lead_overrides : [],
  };
};

const activeLocationId = () => {
  const raw = (sessionStorage.getItem("currentLocationId") || "").split(",")[0];
  const n = Number(raw);
  return Number.isNaN(n) || !n ? null : n;
};

const useReplyAsPreferences = () => {
  const dispatch = useDispatch();
  const locationId = activeLocationId();

  const [saveReplyAsPreference] = useMutation(SAVE_REPLY_AS_PREFERENCE);
  const { data, loading: prefsLoading } = useQuery(REPLY_AS_PREFERENCE, {
    skip: !locationId,
    variables: { locationId },
    fetchPolicy: "cache-first",
  });

  const prefs = useMemo(() => parsePrefs(data?.replyAsPreference), [data]);

  // Saved default rule for an opportunity type: { rule_type, opportunity_user_type_id? } | null.
  const getDefaultRule = useCallback(
    opportunityTypeId => {
      if (opportunityTypeId == null) return null;
      return (
        (prefs.default_rules || []).find(r => +r?.opportunity_type_id === +opportunityTypeId) ||
        null
      );
    },
    [prefs]
  );

  const saveDefaultRule = useCallback(
    async ({ opportunityTypeId, ruleType, opportunityUserTypeId }) => {
      if (!locationId || opportunityTypeId == null) return;
      try {
        const res = await saveReplyAsPreference({
          variables: {
            locationId,
            opportunityTypeId: +opportunityTypeId,
            ruleType,
            // Only sent for OPPORTUNITY_USER_TYPE; otherwise null.
            opportunityUserTypeId:
              ruleType === REPLY_RULE_TYPE.OPPORTUNITY_USER_TYPE && opportunityUserTypeId != null
                ? +opportunityUserTypeId
                : null,
          },
          // Write the merged preferences the server returns back into the query
          // cache so the UI reflects it without a refetch.
          update: (cache, { data: resData }) => {
            const returned = resData?.saveReplyAsPreference?.preferences;
            if (returned == null) return;
            cache.writeQuery({
              query: REPLY_AS_PREFERENCE,
              variables: { locationId },
              data: { replyAsPreference: returned },
            });
          },
        });
        const result = res?.data?.saveReplyAsPreference;
        const code = result?.statusCode;
        if (code >= 200 && code < 300) {
          message.success(result?.message || "Reply-as preference saved");
        } else {
          message.error(result?.message || "Failed to save reply-as preference");
        }
      } catch (e) {
        // Non-fatal — surface via toast + console; the UI keeps its selection.
        message.error("Failed to save reply-as preference");
        // eslint-disable-next-line no-console
        console.error("useReplyAsPreferences: failed to save preference", e);
      }
    },
    [saveReplyAsPreference, locationId]
  );

  // Per-lead override (unchanged). Null value == "no override" (slice merges).
  const overrides = useSelector(state => state.persistedFilters?.filters?.[OVERRIDE_MODULE] || {});
  const getOverride = useCallback(
    leadId => (leadId == null ? null : overrides[String(leadId)] ?? null),
    [overrides]
  );
  const setOverride = useCallback(
    (leadId, replyAsUserId) => {
      if (leadId == null) return;
      dispatch(
        setModuleFilters({
          moduleKey: OVERRIDE_MODULE,
          filters: { [String(leadId)]: replyAsUserId ?? null },
        })
      );
    },
    [dispatch]
  );
  const clearOverride = useCallback(leadId => setOverride(leadId, null), [setOverride]);

  return {
    prefsLoading,
    getDefaultRule,
    saveDefaultRule,
    getOverride,
    setOverride,
    clearOverride,
  };
};

export default useReplyAsPreferences;
